import React, { useState } from "react";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import {
  SelectElement,
  TextareaAutosizeElement,
  TextFieldElement,
  useForm,
} from "react-hook-form-mui";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useErrorLogging } from "../hooks/useErrorLogging";
import { API, graphqlOperation } from "aws-amplify";
import { getLLMPrompt } from "../graphql/queries";
import { GetLLMPromptQuery } from "../API";
import { GraphQLResult } from "@aws-amplify/api";
import { useUser } from "../contexts/useUser";
import { updateLLMPromptAfterEdit } from "../dataaccess/updatePromptAfterEdit";
import { createLLMPromptAfterEdit } from "../dataaccess/createLLMPromptAfterEdit";

const schema = z.object({
  id: z.string().optional(),
  projectId: z.string().optional(),
  name: z.string().min(1, "Name is required"),
  status: z.string().min(1, "Status is required"),
  key: z.string().min(1, "Key is required"),
  description: z.string().optional().nullable(),
  prompt: z.string().optional().nullable(),
});

type PromptFormType = z.infer<typeof schema>;

const PromptForm = () => {
  const { id } = useParams<{ id: string }>();
  const { project } = useUser();
  const navigate = useNavigate();
  const { logError } = useErrorLogging();
  const [buttonText, setButtonText] = useState("Copy");

  const queryClient = useQueryClient();
  const mutationUpdateCustomer = useMutation({
    mutationFn: updateLLMPromptAfterEdit,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["prompts", { id: id }] });

      window.history && window.history.length > 1
        ? navigate(-1)
        : navigate("/");
    },
    onError: (error) => {
      logError("Error updating prompt", error as any);
    },
  });

  const mutationCreatePrompt = useMutation({
    mutationFn: createLLMPromptAfterEdit,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["prompts"] });
      window.history && window.history.length > 1
        ? navigate(-1)
        : navigate("/");
    },
    onError: (error) => {
      logError("Error creating prompt", error as any);
    },
  });

  const {
    data: promptData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["prompts", { id: id }],
    queryFn: () =>
      API.graphql(graphqlOperation(getLLMPrompt, { id: id })) as Promise<
        GraphQLResult<GetLLMPromptQuery>
      >,
    enabled: !!id,
  });

  const { handleSubmit, control, reset, setValue, watch } =
    useForm<PromptFormType>({
      values: promptData?.data?.getLLMPrompt || {
        projectId: project?.id,
        name: "",
        status: "active",
        key: "",
        description: "",
        prompt: "",
      },
      resolver: zodResolver(schema),
    });

  if (id && isLoading) return <div>Loading...</div>;
  if (id && isError) return <div>Error loading project</div>;

  const keyValue = watch("key");

  function generateKeyBasedOnName(name: string) {
    return (
      "{{" +
      name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_/, "")
        .replace(/_$/, "") +
      "}}"
    );
  }

  const handleUpdate = (values: PromptFormType) => {
    if (!values) return;

    try {
      mutationUpdateCustomer.mutate({ id: id, ...values } as any);
    } catch (error) {
      logError("Error updating prompt", error as any);
    }
  };

  const handleCreate = (values: PromptFormType) => {
    if (!values) return;
    if (!project) return;

    mutationCreatePrompt.mutate({
      projectId: project?.id,
      ...values,
    });
  };
  const handleClose = () => {
    window.history && window.history.length > 1 ? navigate(-1) : navigate("/");
  };

  const onSubmit = (data: PromptFormType) => {
    if (id) handleUpdate(data);
    else handleCreate(data);
  };

  return (
    <Paper sx={{ margin: 2, padding: 2, borderRadius: 2 }}>
      <Typography padding={2} variant="h6">
        Prompt
      </Typography>
      <Grid container padding={2} spacing={2}>
        <Grid item xs={12} md={4}>
          <TextFieldElement
            control={control}
            name="name"
            label="Name"
            size="small"
            required
            fullWidth
            onChange={(e) => {
              const key = generateKeyBasedOnName(e.target.value);
              setValue("key", key, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SelectElement
            control={control}
            name="status"
            label="Prompt status"
            size="small"
            required
            fullWidth
            options={[
              { id: "active", label: "Active" },
              { id: "disabled", label: "Disabled" },
              { id: "obsolete", label: "Obsolete" },
              { id: "deleted", label: "Deleted" },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Box display="flex" alignItems="center">
            <TextFieldElement
              control={control}
              name="key"
              label="Key (auto-generated base on name)"
              size="small"
              required
              fullWidth
              InputProps={{
                readOnly: true,
              }}
            />
            <Button
              variant="text"
              onClick={() => {
                navigator.clipboard.writeText(keyValue).then(() => {
                  setButtonText("Copied");
                });
                setTimeout(() => setButtonText("Copy"), 2000);
              }}
              sx={{ ml: 1 }}
            >
              {buttonText}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TextFieldElement
            control={control}
            name="description"
            label="Description & Usage Instructions"
            size="small"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextareaAutosizeElement
            control={control}
            rows={10}
            name="prompt"
            label="Prompt Text"
            size="small"
            fullWidth
          />
        </Grid>
      </Grid>
      <Box sx={{ padding: 2 }}>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant={"contained"}
          sx={{ marginRight: 2 }}
        >
          Save and Close
        </Button>
        <Button onClick={() => reset()} variant={"outlined"}>
          Reset
        </Button>
        <Button onClick={handleClose} variant={"outlined"}>
          Cancel
        </Button>
      </Box>
    </Paper>
  );
};

export default PromptForm;
